import {
  DiscountPlanKey,
  PlanDiscount,
  PromotionCodePlanPreview,
  PromotionCodePreview,
} from '../../services/subscriptions';
import { computePlanDiscountedPrice, formatPrice } from '../../utils/pricing';

export const PROMOTION_PLAN_LABELS: Record<DiscountPlanKey, string> = {
  MONTHLY: 'Monthly',
  ANNUAL: 'Annual',
  FIVE_YEAR: 'Student Life',
};

export interface EffectivePlanPricing {
  originalPrice: number;
  discountedPrice: number;
  isDiscountActive: boolean;
  isCustomPromotion: boolean;
  durationType: string | null;
  durationMonths: number | null;
  badgeLabel: string | null;
}

export type PricingCadence = 'monthly' | 'annual' | 'one_time';

export interface TrialPricePresentation {
  pricePrefix: string;
  unitLabel: string;
  priceSuffix: string | null;
}

const minorToMajor = (amountMinor: number): number => amountMinor / 100;

const getAutomaticBadgeLabel = (
  discount: PlanDiscount | null
): string | null => {
  if (discount?.displayPercentOff) {
    return `${discount.displayPercentOff}% OFF`;
  }
  if (discount?.percentOff) return `${discount.percentOff}% OFF`;
  if (discount?.amountOff) return `$${formatPrice(discount.amountOff)} OFF`;
  return null;
};

const getCustomBadgeLabel = (
  preview: PromotionCodePlanPreview
): string | null => {
  if (preview.percentOff) return `${preview.percentOff}% OFF`;
  if (preview.amountOffMinor) {
    const effectiveAmountOffMinor =
      preview.discountedAmountMinor === null
        ? preview.amountOffMinor
        : preview.originalAmountMinor - preview.discountedAmountMinor;

    return `$${formatPrice(minorToMajor(effectiveAmountOffMinor))} OFF`;
  }
  return null;
};

/**
 * Custom promotion pricing wins only for plans Stripe says are eligible.
 * Every other plan continues using its independently resolved automatic
 * discount, so applying a narrow code never makes another plan more expensive.
 */
export const resolveEffectivePlanPricing = (
  basePrice: number,
  automaticDiscount: PlanDiscount | null,
  customPreview?: PromotionCodePlanPreview
): EffectivePlanPricing => {
  if (customPreview?.eligible && customPreview.discountedAmountMinor != null) {
    const originalPrice = minorToMajor(customPreview.originalAmountMinor);
    const discountedPrice = minorToMajor(customPreview.discountedAmountMinor);
    return {
      originalPrice,
      discountedPrice,
      isDiscountActive: discountedPrice < originalPrice,
      isCustomPromotion: true,
      durationType: customPreview.durationType,
      durationMonths: customPreview.durationMonths,
      badgeLabel: getCustomBadgeLabel(customPreview),
    };
  }

  const discountedPrice = computePlanDiscountedPrice(
    basePrice,
    automaticDiscount
  );

  return {
    originalPrice: basePrice,
    discountedPrice,
    isDiscountActive: discountedPrice < basePrice,
    isCustomPromotion: false,
    durationType: automaticDiscount?.durationType ?? null,
    durationMonths: automaticDiscount?.durationMonths ?? null,
    badgeLabel: getAutomaticBadgeLabel(automaticDiscount),
  };
};

export const getPromotionCodeForPlan = (
  preview: PromotionCodePreview | null,
  plan: DiscountPlanKey
): string | undefined =>
  preview?.plans[plan]?.eligible ? preview.normalizedCode : undefined;

export const getEligiblePromotionPlanLabels = (
  preview: PromotionCodePreview
): string[] =>
  (Object.keys(PROMOTION_PLAN_LABELS) as DiscountPlanKey[])
    .filter((plan) => preview.plans[plan].eligible)
    .map((plan) => PROMOTION_PLAN_LABELS[plan]);

export const getMonthlyPostPromotionLabel = (
  durationType: string | null,
  durationMonths: number | null
): string => {
  if (durationType === 'once') return 'after the first month';
  if (durationMonths) {
    return `after ${durationMonths} ${durationMonths === 1 ? 'month' : 'months'}`;
  }
  return 'after the promotion';
};

const getCadenceUnitLabel = (cadence: PricingCadence): string => {
  if (cadence === 'monthly') return '/mo';
  if (cadence === 'annual') return '/yr';
  return ' once';
};

/**
 * Describes the charge after a free trial. A temporary discount keeps its
 * duration in the pill ("for 3 months") so it never reads as the ongoing rate;
 * the later regular price is deliberately not named (ORA-377). Student Life is
 * a single payment, so a coupon's Stripe duration does not introduce a later
 * price.
 */
export const getTrialPricePresentation = (
  pricing: EffectivePlanPricing,
  cadence: PricingCadence
): TrialPricePresentation => {
  const unitLabel = getCadenceUnitLabel(cadence);

  const isTemporaryRecurringDiscount =
    cadence !== 'one_time' &&
    pricing.isDiscountActive &&
    pricing.durationType !== 'forever';

  if (!isTemporaryRecurringDiscount) {
    return {
      pricePrefix: 'then',
      unitLabel,
      priceSuffix: null,
    };
  }

  if (pricing.durationType === 'repeating') {
    const durationMonths = pricing.durationMonths;

    return {
      pricePrefix: '',
      unitLabel,
      priceSuffix: durationMonths
        ? cadence === 'monthly'
          ? `for ${durationMonths} ${durationMonths === 1 ? 'month' : 'months'}`
          : 'for the first year'
        : 'during the promotion',
    };
  }

  return {
    pricePrefix: 'first payment',
    unitLabel: '',
    priceSuffix: null,
  };
};
